import { ref, onMounted, onUnmounted } from "vue";

const BEACON_SCRIPT_ID = "helpscout-beacon-script";
const beaconScriptUrl = "https://beacon-v2.helpscout.net";

/** Declare global Beacon object for TypeScript */
declare global {
    interface Window {
        Beacon?: {
            (method: string, options?: any, data?: any): void;
            readyQueue?: Array<{
                method: string;
                options?: any;
                data?: any;
            }>;
        };
    }
}

/**
 * Waits for the HelpScout Beacon container to be added to the DOM and adds a "no-print" class to it.
 * This ensures that the Beacon widget does not appear when printing the page.
 * @returns {void}
 */
function waitForBeaconContainer(): void {
    const attempt = () => {
        const el = document.querySelector<HTMLElement>(".BeaconFabButtonFrame");
        if (el) {
            el.classList.add("no-print");
        } else {
            setTimeout(attempt, 100);
        }
    };
    attempt();
}

/**
 * Custom Vue composable to manage the loading and unloading of the HelpScout Beacon widget.
 * This composable handles the dynamic loading of the Beacon script, initializes the widget with the provided Beacon ID,
 * and ensures that the widget is properly cleaned up when the component is unmounted.
 * @param {string} beaconId - The HelpScout Beacon ID
 * @returns {object} - An object containing the loaded state and load/unload functions
 */
export function useHelpScoutBeacon(beaconId: string) {
    const loaded = ref(false);

    // Load the Beacon script if it doesn't exist
    function setupBeaconLoader() {
        if (window.Beacon) return;

        (function (e: Window, t: Document, n: any) {
            function a() {
                if (t.getElementById(BEACON_SCRIPT_ID)) return;

                const script = t.createElement("script");
                script.id = BEACON_SCRIPT_ID;
                script.type = "text/javascript";
                script.async = true;
                script.src = beaconScriptUrl;
                script.classList.add("no-print");

                t.head.appendChild(script);
            }

            e.Beacon = n = function (
                method: string,
                options?: any,
                data?: any,
            ) {
                e.Beacon!.readyQueue!.push({ method, options, data });
            };

            n.readyQueue = [];

            if (t.readyState === "complete") {
                a();
            } else {
                e.addEventListener("load", a, false);
            }
        })(window, document, window.Beacon || function () {});
    }

    // Initialise Beacon
    function load() {
        // Prevent loading HelpScout Beacon during Cypress tests
        if (typeof window !== "undefined" && (window as any).Cypress) {
            loaded.value = false;
            return;
        }

        setupBeaconLoader();

        function onReady() {
            loaded.value = true;
            waitForBeaconContainer();
            window.Beacon && window.Beacon("off", "ready", onReady);
        }

        function tryInitBeacon() {
            if (typeof window.Beacon === "function") {
                window.Beacon("init", beaconId);
                window.Beacon("on", "ready", onReady);
            } else {
                let tries = 0;
                function check() {
                    if (typeof window.Beacon === "function") {
                        window.Beacon("init", beaconId);
                        window.Beacon("on", "ready", onReady);
                    } else if (++tries <= 50) {
                        setTimeout(check, 100);
                    } else {
                        loaded.value = false;
                    }
                }
                check();
            }
        }

        tryInitBeacon();
    }

    // Properly unload Beacon
    function unload() {
        window.Beacon?.("destroy");

        const script = document.getElementById(BEACON_SCRIPT_ID);
        script?.remove();

        delete window.Beacon;

        loaded.value = false;
    }

    onMounted(load); // Load Beacon on component mount
    onUnmounted(unload); // Unload Beacon on component unmount

    return { loaded, load, unload };
}
